import { useEffect, useRef, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { IonPage, IonContent } from "@ionic/react";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { useStore } from "../store/useStore";

export default function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, initialized } = useAuth();
  const history = useHistory();
  const acceptTagShareInvite = useStore((s) => s.acceptTagShareInvite);

  const [status, setStatus] = useState<"loading" | "success" | "error">("loading");
  const [tagName, setTagName] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  // Guard against React Strict Mode double-invocation on mount
  const acceptedRef = useRef(false);

  useEffect(() => {
    if (!initialized) return;

    if (!isAuthenticated) {
      sessionStorage.setItem("pendingInviteToken", token);
      history.replace("/login");
      return;
    }

    // Prevent double-invocation (React Strict Mode runs effects twice on mount)
    if (acceptedRef.current) return;
    acceptedRef.current = true;

    const accept = async () => {
      const result = await acceptTagShareInvite(token);

      if (result.success) {
        setTagName(result.tag_name ?? null);
        setStatus("success");
        setTimeout(() => history.replace("/dump"), 2000);
      } else {
        setErrorMsg(result.error ?? "Failed to accept invite");
        setStatus("error");
      }
    };

    accept();
  }, [initialized, isAuthenticated, token, history, acceptTagShareInvite]);

  return (
    <IonPage>
      <IonContent>
        <div className="min-h-screen bg-background flex items-center justify-center px-6">
          <div className="max-w-sm w-full text-center space-y-4">
            {status === "loading" && (
              <>
                <Loader2 className="w-10 h-10 text-primary animate-spin mx-auto" />
                <p className="text-muted-foreground">Accepting invite...</p>
              </>
            )}
            {status === "success" && (
              <>
                <CheckCircle2 className="w-10 h-10 text-green-500 mx-auto" />
                <p className="text-foreground font-medium">
                  You now have access to {tagName ? `"${tagName}"` : "the shared tag"}!
                </p>
                <p className="text-sm text-muted-foreground">Redirecting...</p>
              </>
            )}
            {status === "error" && (
              <>
                <XCircle className="w-10 h-10 text-destructive mx-auto" />
                <p className="text-foreground font-medium">Could not accept invite</p>
                <p className="text-sm text-muted-foreground">{errorMsg}</p>
                <button
                  onClick={() => history.replace("/dump")}
                  className="mt-4 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-sm font-medium"
                >
                  Go to Brain Dump
                </button>
              </>
            )}
          </div>
        </div>
      </IonContent>
    </IonPage>
  );
}
